/**
 * LoginPage — email + password against `POST /api/auth/login`.
 *
 * The backend sets an HttpOnly session cookie and a readable CSRF cookie; the
 * axios client in `lib/api` attaches the CSRF header on every unsafe method.
 * There is no token in localStorage by design — see `lib/api` for why the
 * client falls back to same-origin requests when BACKEND_URL is cross-origin.
 */
import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiErrorMessage } from "../lib/api";
import AuthPage from "../components/ui/auth-page";
import { isEmbedded } from "../lib/embedBridge";

const HOME = "/app/production/listicle-builder";

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!loading && user) {
    const dest = location.state?.from?.pathname || HOME;
    return <Navigate to={dest} replace />;
  }

  // Never a login form inside the dashboard's CRM frame: EmbedBridge has asked
  // the dashboard for a fresh sign-in.
  if (isEmbedded()) {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-muted-foreground" data-testid="embed-signing-in">
        <div className="text-sm">Signing you in…</div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await login(email.trim(), password);
      navigate(location.state?.from?.pathname || HOME, { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, "Sign-in failed"));
    } finally {
      setBusy(false);
    }
  };

  // Operator-UI sign-in page (PAGES.md 07). AuthPage is presentation only:
  // the state, the submit handler above and the redirects stay here, so the
  // login call, the post-login redirect and the SSO arrival path are unchanged.
  return (
    <AuthPage
      email={email}
      password={password}
      onEmailChange={(e) => setEmail(e.target.value)}
      onPasswordChange={(e) => setPassword(e.target.value)}
      onSubmit={submit}
      busy={busy}
      error={error}
    />
  );
}
